import React, { useEffect, useState, useRef, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";

import RetroGridBg from "../components/RetroGridBg";
import companyLogo from "../assets/nav-lexmom.webp";
import signupImg from "../assets/sign-up.webp";
import wave_lins from "../assets/wave-line.png";
import { backend_api } from "../static/server";
import { CountryCodeSelect, debounce } from "../styles/styles";
import { isDevMode } from "../components/Auth/SubscriptionContext";

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const firmSizeOptions = [
  { value: 1, label: "1–5 lawyers" },
  { value: 5, label: "6–20 lawyers" },
  { value: 20, label: "21–50 lawyers" },
  { value: 50, label: "50+ lawyers" },
];

const StartTrialPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("+1");
  const [userExist, setUserExist] = useState(null); // null = unknown, true = exists, false = available
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);

  const hasFetchedPlan = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      firm_size: "",
      mobile: "",
      subscription_id: "",
    },
  });

  const emailValue = watch("email");

  // Fetch the "advance monthly" plan
  useEffect(() => {
    if (hasFetchedPlan.current) return;
    hasFetchedPlan.current = true;

    const fetchPlan = async () => {
      try {
        const res = await axios.get(
          `${backend_api}plans/?billing_cycle=monthly`,
        );
        const plans = res.data.results || [];

        const targetPlan = plans.find(
          (p) =>
            p.name?.toLowerCase().includes("advance") &&
            p.billing_cycle?.toLowerCase() === "monthly",
        );

        if (targetPlan) {
          setPlan(targetPlan);
          setValue("subscription_id", targetPlan.id);
        } else {
          const fallbackPlan = plans.find((p) => p.one_time_payment);
          if (fallbackPlan) {
            setPlan(fallbackPlan);
            setValue("subscription_id", fallbackPlan.id);
          } else {
            toast.error("Required plan not found");
          }
        }
      } catch (error) {
        toast.error("Failed to load plan details");
        console.error(error);
      } finally {
        setPlanLoading(false);
      }
    };

    fetchPlan();
  }, [setValue]);

  // Debounced email existence check
  const handleCheckEmail = useCallback(
    debounce(async (email) => {
      if (!email || !EMAIL_REGEX.test(email)) {
        setUserExist(null);
        return;
      }

      setEmailCheckLoading(true);
      try {
        const response = await axios.get(
          `${backend_api}user/check-mail/?email=${email}`,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          },
        );
        setUserExist(response?.data?.exist);
      } catch (error) {
        console.error("Error checking email:", error);
        toast.error("Error checking email availability");
      } finally {
        setEmailCheckLoading(false);
      }
    }, 300),
    [],
  );

  // Handle country code change
  const handleCountryChange = (newCode) => {
    setSelectedCountry(newCode);
  };

  // Form submission
  const onSubmit = async (data) => {
    if (userExist === true) {
      toast.error("This email is already registered. Please sign in instead.");
      return;
    }

    setSubmitLoading(true);
    try {
      const payload = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        firm_size: data.firm_size,
        phone_no: selectedCountry + data.mobile,
        subscription_id: data.subscription_id,
      };

      await axios.post(`${backend_api}free-trail/`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success("Trial started successfully! Check your email.");
      navigate(`/trial-success/${emailValue}`);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Failed to start trial. Please try again.";
      toast.error(msg);
      console.error("Trial signup error", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleFocus = () => {
    const currentValue = getValues("email");
    if (!currentValue) {
      setValue("email", " ");
    }
  };

  const handleBlur = (event) => {
    const { value } = event.target;
    setValue("email", value.trim());
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column-reverse", md: "row" },
        p: 0,
      }}
    >
      {/* Left column – form */}{" "}
      {!isSmallScreen && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          sx={{
            width: 1 / 2,
            position: "relative",
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            p: 3,
          }}
        >
          <Box
            sx={{
              width: 1,
              height: 1,
              position: "absolute",
              background:
                "linear-gradient(-125deg, #472ed100 0%, #472ed1 100%)",
              zIndex: 0,
            }}
          >
            <img
              src={wave_lins}
              alt="Wave"
              style={{
                height: "100%",
                width: "100%",
                objectFit: "cover",
                mixBlendMode: "overlay",
              }}
              onError={(e) => {
                e.onerror = null;
                e.target.src = require("../assets/wave-line.png");
              }}
            />
          </Box>

          <Box
            sx={{
              width: 1,
              height: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
              color: "white",
              textAlign: "center",
              p: 3,
            }}
          >
            <Box sx={{ height: "20rem", mb: 4 }}>
              <img
                src={signupImg}
                alt="Trial illustration"
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "contain",
                  filter: "brightness(1.1)",
                }}
                onError={(e) => {
                  e.onerror = null;
                  e.target.src = require("../assets/no-preview.webp");
                }}
              />
            </Box>
            <Typography
              variant="body1"
              sx={{ mb: 1, fontSize: "1.4rem", fontWeight: 600 }}
            >
              Try LexMom Free for 14 Days
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
              Experience all premium features. No commitment, cancel anytime.
            </Typography>
          </Box>
        </Box>
      )}
      {/* Right column – illustration (hidden on mobile) */}{" "}
      <Box
        component={motion.div}
        initial={{ opacity: 0, x: -100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
        sx={{
          width: { xs: 1, md: 1 / 2 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 2, md: 3 },
          overflow: "hidden",
          position: "relative",
          borderRadius: { xs: "1rem", md: "2rem" },
          minHeight: { xs: "100vh", md: "auto" },
        }}
      >
        <Box
          sx={{
            width: 1,
            maxWidth: "27.5rem",
            p: { xs: 2, md: 3 },
            zIndex: 5,
            borderRadius: 2,
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0px 0px 2rem #e5e5e5",
          }}
        >
          {/* Logo and header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: { xs: 2, md: 4 },
            }}
          >
            <Box
              sx={{
                height: { xs: "2.5rem", md: "3rem" },
                mb: 1,
                cursor: "pointer",
              }}
              onClick={() => navigate("/")}
            >
              <img
                src={companyLogo}
                alt="Company Logo"
                style={{ height: "100%", width: "auto", objectFit: "contain" }}
                onError={(e) => {
                  e.onerror = null;
                  e.target.src = require("../assets/nav-lexmom.webp");
                }}
              />
            </Box>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
              Start Your Free Trial
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No credit card required. 14 days free.
            </Typography>
          </Box>

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: "1rem", md: "0.9rem" },
              width: 1,
            }}
          >
            {/* First name & last name row */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <TextField
                size="small"
                fullWidth
                label="First Name"
                variant="outlined"
                error={!!errors.first_name}
                helperText={errors.first_name?.message}
                {...register("first_name", {
                  required: "First name is required",
                })}
              />
              <TextField
                size="small"
                fullWidth
                label="Last Name"
                variant="outlined"
                error={!!errors.last_name}
                helperText={errors.last_name?.message}
                {...register("last_name", {
                  required: "Last name is required",
                })}
              />
            </Box>

            {/* Email with existence check */}
            <Box>
              <TextField
                size="small"
                fullWidth
                label="Email"
                type="email"
                variant="outlined"
                error={!!errors.email || userExist === true}
                helperText={
                  errors.email?.message ||
                  (userExist === true && "This email is already registered")
                }
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: EMAIL_REGEX,
                    message: "Invalid email address",
                  },
                })}
                InputLabelProps={{
                  shrink: !!emailValue || emailValue === " ",
                }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={(e) => handleCheckEmail(e.target.value)}
              />
              {userExist === true && (
                <Alert
                  severity="info"
                  sx={{ mt: 1, py: 0, fontSize: "0.875rem" }}
                >
                  Already have an account?{" "}
                  <Box
                    component="span"
                    sx={{
                      textDecoration: "underline",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/signin")}
                  >
                    Sign in here
                  </Box>
                </Alert>
              )}
            </Box>

            {/* Firm size dropdown */}
            <TextField
              size="small"
              fullWidth
              select
              label="Firm Size"
              variant="outlined"
              error={!!errors.firm_size}
              helperText={errors.firm_size?.message}
              {...register("firm_size", {
                required: "Please select firm size",
              })}
            >
              {firmSizeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Phone number with country code select */}
            <Box display="flex" alignItems="flex-start" width="100%">
              <CountryCodeSelect
                value={selectedCountry}
                onChange={handleCountryChange}
              />
              <Controller
                name="mobile"
                control={control}
                rules={{
                  required: "Mobile number is required",
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Please enter a valid 10-digit mobile number",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    type="tel"
                    fullWidth
                    label="Mobile Number"
                    error={!!errors.mobile}
                    helperText={errors.mobile?.message}
                    sx={{
                      "& .MuiInputBase-input": {
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      },
                      "& .MuiOutlinedInput-root": {
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      },
                    }}
                  />
                )}
              />
            </Box>

            {/* Hidden subscription_id field */}
            <input type="hidden" {...register("subscription_id")} />

            {/* Selected plan summary */}
            {plan && isDevMode && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                You are signing up for the <strong>{plan.name}</strong> plan (
                {plan.billing_cycle}).
              </Typography>
            )}

            {/* Submit button */}
            <LoadingButton
              type="submit"
              loading={submitLoading || planLoading || emailCheckLoading}
              loadingPosition="start"
              startIcon={<BusinessCenterRoundedIcon />}
              variant="contained"
              fullWidth
              size="medium"
              sx={{ mt: 2 }}
              disabled={userExist === true}
            >
              {planLoading
                ? "Loading plan..."
                : userExist === true
                  ? "Email already registered"
                  : "Start Trial"}
            </LoadingButton>

            {/* Back to login link */}
            <Box sx={{ textAlign: "center", mt: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => navigate("/signin")}
              >
                Already have an account? Sign in
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StartTrialPage;
